/**
 * ImageLightbox - Reusable fullscreen image viewer overlay
 * Displays an image at full size with optional action buttons (delete, crop, etc.)
 */

import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * Props for the ImageLightbox component
 */
interface ImageLightboxProps {
  /** Image source URL */
  imageUrl: string;
  /** Accessibility alt text */
  alt: string;
  /** Whether the lightbox is visible */
  open: boolean;
  /** Callback to dismiss the lightbox */
  onClose: () => void;
  /** Optional action buttons (delete, crop, etc.) rendered at bottom */
  actions?: React.ReactNode;
  /** Test ID for the overlay, defaults to "image-lightbox" */
  testId?: string;
}

/**
 * Fullscreen image lightbox with dismissal via overlay click or Escape key.
 * Renders an optional actions bar at the bottom for contextual buttons.
 * Uses z-[60] to sit above drawer content (z-[51]).
 */
export function ImageLightbox({
  imageUrl,
  alt,
  open,
  onClose,
  actions,
  testId = 'image-lightbox'
}: ImageLightboxProps) {
  const overlayRef = useRef<HTMLDivElement>(null);

  /** Auto-focus the overlay on mount so keyboard events are captured */
  useEffect(() => {
    if (open && overlayRef.current) {
      overlayRef.current.focus();
    }
  }, [open]);

  if (!open) {
    return null;
  }

  /** Handle keydown — close on Escape */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  /** Prevent clicks on inner content from closing the lightbox */
  const handleContentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return createPortal(
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm"
      onClick={onClose}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-label="Image viewer"
      data-testid={testId}
      tabIndex={0}
    >
      {/* Image — clicks here should NOT close the lightbox */}
      <img
        src={imageUrl}
        alt={alt}
        className="max-h-[90vh] max-w-[90vw] object-contain"
        onClick={handleContentClick}
      />

      {/* Actions bar at bottom center */}
      {actions && (
        <div
          className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-3 bg-black/50 backdrop-blur-sm rounded-full px-4 py-2"
          onClick={handleContentClick}
        >
          {actions}
        </div>
      )}
    </div>,
    document.body
  );
}

export default ImageLightbox;
